import type { WrittenPage } from '../../sections'
import { PARTNER_FORM_TR } from '../../partnerForm'

/**
 * İş Ortaklığı Programı — üst menüdeki "İş Ortakları" bağlantısının açtığı sayfa.
 *
 * 2026-08-27'ye kadar WordPress'ten aktarılmış düz bir metindi; o gün
 * digitopia.co'nun kendi partner sayfasının ekran görüntüleriyle birlikte
 * yeniden tasarlanması istendi. Bölüm sırası, iddialar ve sayılar İngilizcesiyle
 * aynı; değişen yalnızca nasıl söylendiği.
 *
 * Alıntılar Türkçeye çevrildi — ana sayfadaki müşteri alıntılarında olduğu gibi
 * — ama kim söylediyse o kalıyor: isim, unvan ve şirket çevrilmez.
 *
 * Amber notlar (`check`) bilerek yalnızca İngilizce dosyada: sorular aynı
 * sorular, iki dilde iki kez sayılmaları listeyi okunmaz hale getirirdi.
 */
export const partnerProgram: WrittenPage = {
  navLabel: 'İş Ortaklığı Programı',
  title: 'İş Ortaklığı Programı',
  description:
    'Digitopia İş Ortaklığı Programı: Impact Platform ile dönüşüm projeleri yürütün, olgunluk endeksi practitioner sertifikası alın, yeni bir gelir kalemi açın.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'İş Ortakları',
      headline: 'Digitopia ile birlikte büyüyün: İş Ortaklığı Programı’na katılın.',
      sub: 'Danışmanlığı birlikte yeniden kurgulayalım, müşterilerinizi memnun edelim.',
      photo: {
        src: '/photo/partner-handshake-celebration.webp',
        alt: 'Cam bölmeli bir ofiste el sıkışan iki danışman ve onları alkışlayan ekip arkadaşları.',
      },
      ctas: [{ label: 'İş ortağı olun', href: '#contact' }],
    },
    {
      kind: 'prose',
      paragraphs: [
        'Digitopia, 20’den fazla ülkede kullanılan dönüşüm yönetimi yazılımı Impact Platform ile danışmanlık sektörünü yeniden kuruyor. Şirketleri ve danışmanları daha iyi bir dönüşüm sürecine yönlendiriyoruz.',
        'İş birliğinin gücüne inanıyoruz. İş Ortaklığı Programı, hem sizin hem müşterilerinizin kazandığı ilişkiler kurmak için tasarlandı. Bir danışmanlık şirketi, bir teknoloji sağlayıcısı ya da sektörünüzde ses getiren bir isim olun; bizimle iş ortaklığı, dijital dönüşümü birlikte yürüten bir ağa katılmak demek.',
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Neden Digitopia ile iş ortaklığı?',
      sub: 'Bu ortaklığın elinize verdiği üç şey.',
    },
    {
      kind: 'split',
      flip: true,
      heading: 'Yenilikçi çözümler',
      paragraphs: [
        'Excel tablolarını geride bırakın. Platform, iş dönüşümünü yönetmek için kapsamlı ama kullanması kolay bir yaklaşım sunuyor; iş ortağı olarak kendi müşteri projelerinizde Impact Platform’a erişiyorsunuz.',
      ],
      photo: {
        src: '/photo/impact-platform-target-state.webp',
        alt: 'Impact Platform hedef durum ekranı: olgunluk puanı, boyut bazında skorlar ve sektör kıyaslaması yan yana.',
      },
      cta: { label: 'İş ortağı olun', href: '#contact' },
    },
    {
      kind: 'split',
      heading: 'Uzmanlığınızı derinleştirin',
      paragraphs: [
        'Özel eğitim programımızla sertifikalı Digital Maturity Index Practitioner olun; dönüşüm danışmanlığını kendi markanız altında verebilecek donanıma kavuşun.',
      ],
      photo: {
        src: '/photo/daimi-score-presentation.jpg',
        alt: 'Toplantı odasındaki ekranda DAIMI puanını ve kıyaslamasını üç meslektaşına anlatan bir danışman.',
      },
      cta: { label: 'DAIMI eğitimi', href: '/en/digital-ai-maturity-index-daimi-course' },
    },
    {
      kind: 'split',
      flip: true,
      heading: 'Rekabet avantajı ve gelir artışı',
      paragraphs: [
        'Dünya genelinde kurumların güvendiği dönüşüm yönetimi yazılımını sunarak sektörünüzde farklılaşın; esnek iş ortaklığı modelleriyle yeni bir gelir kalemi açın.',
      ],
      photo: {
        src: '/photo/team-applause.jpg',
        alt: 'Toplantı masasının etrafında, arkalarındaki beyaz tahtadaki yol haritasının önünde alkışlayan proje ekibi.',
      },
      cta: { label: 'İş ortağı olun', href: '#contact' },
    },
    // Aynı bölüm, aynı kart — 2026-08-28. `variant`, `company`, `logo` ve
    // `portrait` İngilizcesiyle birebir aynı: bir dizi bütünüyle verilir ya da
    // hiç verilmez (EDITING.md, kural 2), marka adı ile fotoğraf da çevrilecek
    // şeyler değil. Portrenin `alt` metni sadece kişinin adı, o da her dilde
    // aynı — çevirisi olan tek şey alıntının kendisi ve başlık.
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'İş ortaklarımız ne diyor?',
      items: [
        {
          quote:
            'Yapay zeka yalnızca bir teknoloji değişimi değil, bir liderlik meselesi. AIMI ile müşterilerimize, yapay zeka potansiyelini iş değerine çevirmek için ihtiyaç duydukları yapıyı ve görünürlüğü verebiliyoruz.',
          source: 'Russ Ballard, Partner, Fisher Management Partners',
          company: 'Fisher Management Partners',
          logo: { name: 'Fisher Management Partners', src: '/logo/fishermanagementpartners.webp' },
          portrait: { src: '/photo/portrait-russ-ballard.webp', alt: 'Russ Ballard' },
        },
        {
          quote:
            'Digitopia’nın Impact Platform’u karmaşıklığı netliğe çevirerek müşterilerimize daha fazla değer sunmamızı sağlıyor. Yapılandırılmış olgunluk değerlendirmeleri ve net yol haritalarıyla liderlerin önce neye odaklanacağını belirlemesine, ekipleri hizalamasına ve ölçülebilir dönüşüm sonuçlarına ulaşmasına yardımcı oluyoruz.',
          source: 'Peter Meyers, CMO, MSS Business Transformation Advisory',
          company: 'MSS Business Transformation Advisory',
          logo: { name: 'MSS Business Transformation Advisory', src: '/logo/mssbta.webp' },
          portrait: { src: '/photo/portrait-peter-meyers.webp', alt: 'Peter Meyers' },
        },
        {
          quote:
            'Digitopia ile iş ortaklığımız NexGen için dönüştürücü oldu. Impact Platform hizmetimizin temel taşı haline geldi: tüm paydaşları bir araya getiriyor, otomatik ve etkili çalışıyor, karmaşık süreçleri sadeleştiriyor ve pazarın ihtiyacına uyuyor.',
          source: 'Jose Antonio De La Ossa Duarte, CEO, NexGen',
          company: 'NexGen',
          logo: { name: 'NexGen', src: '/logo/nexgen.webp' },
          portrait: {
            src: '/photo/portrait-jose-antonio-de-la-ossa-duarte.webp',
            alt: 'Jose Antonio De La Ossa Duarte',
          },
        },
      ],
    },
    {
      kind: 'cards',
      heading: 'İş ortağı olun',
      sub: 'Üç ortaklık modeli. Hangisi olduğunuzu söyleyin, gerisini birlikte kuralım.',
      buttonCtas: true,
      ctaAlignLeft: true,
      items: [
        {
          title: 'Consulting Partners',
          promise:
            'Bu program özellikle serbest çalışan danışmanlar ve butik danışmanlık şirketleri için tasarlandı.',
          points: [],
          cta: { label: 'İş ortağı olun', href: '#contact' },
        },
        {
          title: 'Solution Partners',
          promise:
            'Digitopia çözümlerini satmak iyi bir iş; hâlihazırda yürüttüğünüz işi de büyütür.',
          points: [],
          cta: { label: 'İş ortağı olun', href: '#contact' },
        },
        {
          title: 'Platform Partners',
          promise:
            'Danışmanlığı bizim platformumuz üzerinde yeniden kurgulamak istiyorsanız kesinlikle konuşmalıyız.',
          points: [],
          cta: { label: 'İş ortağı olun', href: '#contact' },
        },
      ],
    },
    // Sayfanın kendi formu ve sayfadaki her "İş ortağı olun" butonunun indiği
    // `#contact`. Alanlar kapanış bandındakilerin aynısı, üstüne bu sayfanın
    // ihtiyaç duyduğu iki alan — bkz. `content/partnerForm.ts`.
    {
      kind: 'form',
      heading: 'İletişime geçelim!',
      ...PARTNER_FORM_TR,
    },
  ],
}
